import { useQuery } from "@apollo/client";
import {
  Button,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  makeStyles,
  Switch,
  Typography,
} from "@material-ui/core";
import { Link } from "@resource/atlas";
import { useFlags } from "@resource/client-ffs";
import loginWithPopUp from "auth/loginWithPopUp";
import IntegrationModal from "components/legacy/Integrations/IntegrationModal";
import Loading from "components/legacy/Loading/Loading";
import FeatureFlags from "generated/FeatureFlags";
import {
  SlackIntegrationForCustomerPrisma,
  SlackIntegrationForCustomerPrisma_currentUserV2_organization_customer_integration as SlackIntegration,
  SyncSlackIntegrationForCustomerPrisma,
  UpdateSlackIntegrationForCustomerPrisma,
  UpdateSlackIntegrationForCustomerPrismaVariables,
} from "generated/schemaTypes";
import slackIconLg from "icons/slack-icon-lg.svg";
import React, { useCallback, useEffect, useState } from "react";
import useMutation from "utils/useMutation";

import {
  SLACK_INTEGRATION_FOR_CUSTOMER_PRISMA,
  SYNC_SLACK_PRISMA,
  UPDATE_SLACK_INTEGRATION_FOR_CUSTOMER_PRISMA,
} from "./PRISMA_SLACK_MUTATIONS";

type SlackIntegrationModalProps = {
  open: boolean;
  onClose: () => void;
};

const useSwitchStyles = makeStyles({
  root: {
    marginRight: 8,
  },
});

const useStyles = makeStyles(() => ({
  actions: {
    padding: "2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  description: {
    marginBottom: "1.5rem",
  },
  checkbox: {
    marginLeft: "0.4rem",
    marginBottom: "0.8rem",
  },
  formAction: {
    width: "10rem",
  },
}));

function SlackIntegrationModal({ open, onClose }: SlackIntegrationModalProps) {
  const { data, refetch } = useQuery<SlackIntegrationForCustomerPrisma>(
    SLACK_INTEGRATION_FOR_CUSTOMER_PRISMA
  );
  const { [FeatureFlags.ENABLE_CHAT_ADDON]: enableChatAddon } = useFlags();

  const [syncSlack] =
    useMutation<SyncSlackIntegrationForCustomerPrisma>(SYNC_SLACK_PRISMA);

  const [updateSlack, { loading: updateSlackLoading }] = useMutation<
    UpdateSlackIntegrationForCustomerPrisma,
    UpdateSlackIntegrationForCustomerPrismaVariables
  >(UPDATE_SLACK_INTEGRATION_FOR_CUSTOMER_PRISMA);

  const styles = useStyles();
  const integration = data?.currentUserV2?.organization?.customer
    .integration as SlackIntegration | null | undefined;

  const [autopopulateInterviewer, setAutopopulateInterviewer] = useState(false);
  const [receiveNotifications, setReceiveNotifications] = useState(false);

  useEffect(() => {
    if (integration) {
      setAutopopulateInterviewer(!!integration.autopopulateInterviewer);
      setReceiveNotifications(!!integration.receiveNotifications);
    }
  }, [integration]);

  const switchClasses = useSwitchStyles();

  const handleConnect = () => {
    loginWithPopUp(
      {
        connection: "slack",
      },
      () => {
        refetch();
      }
    );
  };

  const handleUpdate = useCallback(async () => {
    await updateSlack({
      variables: {
        input: {
          autopopulateInterviewer,
          receiveNotifications,
        },
      },
    });

    onClose();
  }, [autopopulateInterviewer, onClose, receiveNotifications, updateSlack]);

  return (
    <IntegrationModal
      title="Slack"
      logoSrc={slackIconLg.src}
      open={open}
      onClose={onClose}
      {...(integration?.connected
        ? {
            onSave: handleUpdate,
            disabled: false,
            updateSaving: updateSlackLoading,
          }
        : {})}
    >
      <Typography className={styles.description} variant="body1">
        The Guide Slack integration connects basic Interviewer information to
        automatically populate Interviewer names and photos.{" "}
        {enableChatAddon ? (
          <span>
            Additionally, you can receive messages from candidates who
            communicate via the in-Guide chat to a Slack channel.
          </span>
        ) : null}
      </Typography>
      {!!integration?.connected && (
        <FormControl component="fieldset">
          <FormGroup aria-label="position" row>
            <FormControlLabel
              className={styles.checkbox}
              control={
                <Switch
                  className={switchClasses.root}
                  checked={autopopulateInterviewer}
                  onChange={(
                    _e: React.ChangeEvent<HTMLInputElement>,
                    value: boolean
                  ): void => {
                    setAutopopulateInterviewer(value);
                  }}
                  color="primary"
                />
              }
              label="Autopopulate New Interviewers"
              labelPlacement="end"
            />
            {enableChatAddon && (
              <FormControlLabel
                value="end"
                className={styles.checkbox}
                control={
                  <Switch
                    className={switchClasses.root}
                    checked={receiveNotifications}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      value: boolean
                    ): void => {
                      setReceiveNotifications(value);
                    }}
                    color="primary"
                  />
                }
                label="Receive Notifications On Slack"
                labelPlacement="end"
              />
            )}
          </FormGroup>
        </FormControl>
      )}
      {!integration?.connected && updateSlackLoading && (
        <Loading inlineStyling />
      )}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link as="button" onClick={() => syncSlack()}>
        <p className="text-body-md">Re-enrich interviewer profiles</p>
      </Link>
      <DialogActions className={styles.actions}>
        {!integration?.connected && (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleConnect();
            }}
          >
            <img
              alt="Add to Slack"
              height="40"
              width="139"
              src="https://platform.slack-edge.com/img/add_to_slack.png"
              srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
            />
          </Button>
        )}
      </DialogActions>
    </IntegrationModal>
  );
}

export default SlackIntegrationModal;
